/* eslint-disable react-hooks/exhaustive-deps */
import { Row, Switch } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'
import { CustomCol, FormLabels } from 'src/components'
import { getCtCookie } from 'src/core/helpers'
import { useWindowSize } from 'src/app/hooks'
import axios from 'axios'

const ManagePermissionsChangeSubscription: React.FC = () => {
  const { isMobile } = useWindowSize()
  const { t } = useTranslation()
  const [hasSubscription, setHasSubscription] = useState(false)
  const [notificationPushSupported, setPushNotificationSupported] = useState(false)

  const getHasSubscription = async (): Promise<boolean> => {
    const visitorId = getCtCookie('ct_vid') || null
    if (!visitorId) return false

    try {
      const response = await axios.get<{ hasPushSubscription: boolean }>(`${process.env.PUSH_NOTIFICATIONS_URL}/api/v1/notification/hasSubscriptionForVisitorId/${visitorId}`)
      return response.data.hasPushSubscription
    } catch (error) {
      return false
    }
  }

  const unsubscribeVisitor = async (identifier: string): Promise<boolean> => {
    const unsubscribeUrl = `${process.env.PUSH_NOTIFICATIONS_URL}/api/v1/notification/unsubscribe`
    try {
      const { data: { isActive } } = await axios.post(unsubscribeUrl, { identifier })
      return isActive
    } catch (error) {
      return false
    }
  }

  // eslint-disable-next-line complexity
  const handleSwitchChange = async () => {
    if (!hasSubscription) {
      try {
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' })

          // Wait for the service worker to be ready
          await new Promise(resolve => setTimeout(resolve, 150))

          if (!registration) return

          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: process.env.VAPID_PUBLIC_KEY
          })

          if (!subscription) return
          const authKey = subscription.getKey('auth')
          const p256dhKey = subscription.getKey('p256dh')

          if (!authKey || !p256dhKey) return

          const endpoint = subscription.endpoint
          const expirationTime = (subscription as any).expirationTime
          const auth = Buffer.from(Array.from(new Uint8Array(authKey))).toString('base64')
          const p256dh = Buffer.from(Array.from(new Uint8Array(p256dhKey))).toString('base64')

          const languageCode = getCtCookie('ct-language')
          const visitorId = getCtCookie('ct_vid')

          const data = {
            visitorId,
            endpoint,
            expirationTime,
            auth,
            p256dh,
            languageCode
          }

          const url = `${process.env.PUSH_NOTIFICATIONS_URL}/api/v1/notification/`
          const response = await axios.post(url, data)
          const { isActive } = response.data
          setHasSubscription(isActive)
          return
        }
        setHasSubscription(false)
      } catch (error) {
        setHasSubscription(false)
      }
    } else {
      const visitorId = getCtCookie('ct_vid') || null
      let identifier = visitorId
      let isActive = true
  
      try {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (!subscription) return
  
        if (!visitorId) {
          const auth = subscription.getKey('auth')
          identifier = auth ? Buffer.from(String.fromCharCode.apply(null, new Uint8Array(auth))).toString('base64') : null
        }

        subscription.unsubscribe()
  
        if (identifier) {
          isActive = await unsubscribeVisitor(identifier)
        }
  
        setHasSubscription(isActive)
      } catch (error) {
        setHasSubscription(false)
      }
    }
  }

  // Check if browser suppot push notifications
  useEffect(() => {
    if (('serviceWorker' in navigator) && ('PushManager' in window)) {
      setPushNotificationSupported(true)
    }
  }, [])

  useEffect(() => {
    const fetchSubsciption = async () => {
      const hasSub = await getHasSubscription()
      setHasSubscription(hasSub)
    }

    fetchSubsciption()
  }, [])

  return (
    <>
      {notificationPushSupported && (
        <Row className={isMobile && 'mt-5'}>
          <CustomCol xs={19}>
            <FormLabels
              title={t('Permisions.PushNotificationsLabel')}
              subtitle={t('Permisions.PushNotificationsSublabel')}
            />
          </CustomCol>
          <CustomCol xs={5}>
            <Switch
              checked={hasSubscription}
              className={isMobile ? 'mt-2 ml-3' : 'ml-2'}
              checkedChildren={<CheckOutlined />}
              onChange={handleSwitchChange}
            />
          </CustomCol>
        </Row>
      )}
    </>
  )
}

export default ManagePermissionsChangeSubscription
